import { Link } from 'react-router-dom';
import { ArrowRight, Factory, FlaskConical, Handshake, Leaf, MapPin, Quote, ShieldCheck, UsersRound } from 'lucide-react';

import { PageShell } from '../PageShell/index';
import { siteConfig } from '../../shared/data/siteConfig';

const pillars = [
  { title: 'Quality Ingredients', description: 'Raw material checks and responsible sourcing remain central to product quality.', icon: Leaf },
  { title: 'Process Discipline', description: 'Production, packing, storage, and dispatch are managed with a quality-first operating mindset.', icon: Factory },
  { title: 'Quality Assurance', description: 'Product information, batch care, and documentation support dependable customer decisions.', icon: ShieldCheck },
  { title: 'Farmer Focused', description: 'Farmers, dealers, distributors, and bulk buyers are served with clear, practical communication.', icon: UsersRound }
];



export function About() {
  return (
    <PageShell
      title="About Us"
      eyebrow="Who we are"
      path="/about"
      description="Zfast Agritech India is a manufacturer-led agritech brand focused on animal nutrition, farmer trust, product clarity, dealer partnerships, and responsible agritech supply."
      breadcrumbs={[{ label: 'About' }]}
    >
      <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 sm:py-16 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-[1fr_0.9fr] lg:items-center">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.22em] text-gold">Zfast Agritech India</p>
            <h2 className="mt-4 max-w-3xl text-3xl font-black leading-tight tracking-[-0.035em] text-ink sm:text-5xl">
              Building trust through quality, science, and practical farm support.
            </h2>
            <p className="mt-6 max-w-3xl text-base leading-8 text-charcoal/75 sm:text-lg">
              Zfast Agritech India is a dependable agritech brand for premium cattle feed, pig feed, and animal nutrition products. The focus is simple: quality raw materials, disciplined production, clear product communication, and long-term trust with farmers, dealers, retailers, distributors, and bulk buyers.
            </p>
            <p className="mt-4 max-w-3xl text-base leading-8 text-charcoal/75 sm:text-lg">
              The company also supports trade enquiries for edible oils, including mustard oil, soybean refined oil, and sunflower oil, with a customer-first approach for retail, food service, and bulk supply conversations.
            </p>
            <address className="mt-5 flex max-w-3xl items-start gap-3 not-italic text-charcoal/75">
              <MapPin className="mt-1 h-5 w-5 shrink-0 text-forest" aria-hidden="true" />
              <span><strong className="text-ink">Main office:</strong> {siteConfig.address}</span>
            </address>
          </div>

          <div className="overflow-hidden rounded-[1.15rem] bg-white shadow-soft ring-1 ring-forest/10">
            <img src="/images/products/cattle-feed/cattle-feed.webp" alt="Cattle feed and farm-side nutrition" className="h-72 w-full object-cover sm:h-80" loading="eager" width={600} height={320} />
            <div className="grid gap-4 p-6 sm:grid-cols-2">
              <div>
                <p className="text-xs font-black uppercase tracking-[0.18em] text-gold">Our Vision</p>
                <p className="mt-3 text-sm leading-6 text-charcoal/75">To become a trusted agritech and nutrition brand supporting farmers with dependable products and transparent information.</p>
              </div>
              <div>
                <p className="text-xs font-black uppercase tracking-[0.18em] text-gold">Our Mission</p>
                <p className="mt-3 text-sm leading-6 text-charcoal/75">Deliver quality feed products, support dairy and livestock productivity, and build long-term partnerships with farmers, dealers, and distributors.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-ink py-12 text-white sm:py-14">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-xs font-black uppercase tracking-[0.22em] text-[#FFE08A]">Why choose Zfast?</p>
            <h2 className="mt-3 text-3xl font-black sm:text-4xl">Quality. Science. Results.</h2>
          </div>
          <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {pillars.map(({ title, description, icon: Icon }) => (
              <div key={title} className="border-l border-white/20 pl-5 first:border-l-0 first:pl-0">
                <Icon className="h-8 w-8 text-[#FFE08A]" />
                <h3 className="mt-4 text-lg font-black">{title}</h3>
                <p className="mt-3 text-sm leading-6 text-white/72">{description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 sm:py-16 lg:px-8">
        <div className="grid gap-6 md:grid-cols-2 lg:items-stretch">
            <div className="overflow-hidden rounded-[1.15rem] bg-cream ring-1 ring-forest/10">
              <img src="/images/about/zfast-lab-check.webp" alt="Quality check and laboratory process" className="h-64 w-full object-cover" loading="lazy" width={600} height={256} />
              <div className="p-6">
                <FlaskConical className="h-8 w-8 text-forest" />
                <h3 className="mt-4 text-2xl font-black text-ink">Quality-first operating mindset.</h3>
                <p className="mt-3 leading-7 text-charcoal/75">Zfast focuses on raw material quality, production discipline, quality checks, hygienic storage, and dealer-ready supply so customers receive dependable product support.</p>
              </div>
            </div>

            <div className="rounded-[1.15rem] bg-white p-6 shadow-soft ring-1 ring-forest/10">
              <Quote className="h-8 w-8 text-gold" />
              <h3 className="mt-4 text-2xl font-black text-ink">Founder’s message</h3>
              <p className="mt-4 leading-8 text-charcoal/75">
                “Our focus has always been on quality, innovation, and the success of India’s farmers. We believe better nutrition, disciplined production, and stronger farmer support can build a more reliable future for the dairy industry.”
              </p>
              <p className="mt-5 font-black text-forest">Founder & CEO</p>
            </div>
        </div>
      </section>

      <section className="bg-page py-14 sm:py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-4 md:grid-cols-3">
            <img src="/images/farmer-corner/cattle-shed.webp" alt="Animal nutrition facility and cattle shed" className="h-64 w-full rounded-[1rem] object-cover shadow-soft" loading="lazy" width={400} height={256} />
            <img src="/images/about/zfast-lab-check.webp" alt="Quality control lab visual" className="h-64 w-full rounded-[1rem] object-cover shadow-soft" loading="lazy" width={400} height={256} />
            <img src="/images/farmer-corner/cattle-care.webp" alt="Farm support and cattle care" className="h-64 w-full rounded-[1rem] object-cover shadow-soft" loading="lazy" width={400} height={256} />
          </div>
          <div className="mt-8 flex flex-col gap-4 rounded-[1rem] bg-white p-6 shadow-soft ring-1 ring-forest/10 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-2xl font-black text-ink">Want to know more about products or dealership?</h2>
              <p className="mt-2 text-charcoal/70">Share your requirement and our team can guide you with product information, availability, and partnership support.</p>
            </div>
            <Link to="/contact" className="inline-flex items-center justify-center gap-2 rounded-full bg-gold px-6 py-3 font-black text-ink transition hover:-translate-y-0.5 hover:bg-tint">
              Contact Us <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>


    </PageShell>
  );
}
